import type { AbstractControl } from '@angular/forms';

/**
 * Committing a value the USER just produced in a custom field component (#21026).
 *
 * `setValue`/`patchValue` never touch `pristine` — only Angular's ControlValueAccessor pipeline
 * does, and a component that renders its own checkboxes/chips/dialogs and writes the control
 * programmatically bypasses that pipeline entirely. The control then holds the new value while
 * the form still reports `pristine`, so the detail page's save button stays disabled
 * (details.component.ts saveDisabled) and UnSavedChangesGuard lets the user walk away without a
 * warning. That is exactly what bug #21026 reported for "Bedrijf" and "Gebruikersrollen".
 *
 * ORDERING CONTRACT — a form event must fire AFTER the final value is in the control:
 *  - `commitUserValue` marks dirty BEFORE writing, mirroring Angular's own CVA pipeline
 *    (`updateControl` marks dirty and only then calls `setValue`). The value change is the last
 *    event, so anything listening on `form.events` sees the finished state.
 *  - A component that writes the control itself — typically with `{ emitEvent: false }` to avoid
 *    re-entering its own `valueChanges` subscription — must call `markUserEdit` AFTER that write:
 *    there the dirty transition is the only event that fires, and it must fire on the new value.
 *
 * Programmatic writes that are NOT user edits (hydration, normalisation on init, masking) must
 * NOT call either helper: DetailsService keeps its saved-value baseline in sync for as long as the
 * form is pristine, which is what makes "change a field, change it back → save disabled again"
 * work.
 */
export function markUserEdit(control: AbstractControl | null | undefined): void {
  if (!control) {
    return;
  }
  control.markAsDirty();
  control.markAsTouched();
  // `markAsDirty`/`markAsTouched` only emit on the pristine->dirty and untouched->touched
  // TRANSITION (AbstractControl guards both on `changed`), so from the second edit onwards they
  // are silent. Combined with a caller that wrote the value with `{ emitEvent: false }` that left
  // the whole edit invisible: DetailsService's bridge listens on the ROOT form, which then never
  // recomputed, and typing a value back to its original left the save button enabled.
  //
  // Recomputing from the PARENT lets the change ripple up to the root — every ancestor re-reads
  // its children and emits — while the edited control itself stays silent. That last part is
  // load-bearing for the autocomplete: its own `valueChanges` subscription rewrites the visible
  // search text from the debounced value, which would eat characters typed during the debounce
  // window. That is why the caller silenced the write in the first place.
  control.parent?.updateValueAndValidity();
}

/**
 * Write a user-produced value and mark the control as edited. See the ordering contract above for
 * when to use this instead of writing the control yourself plus `markUserEdit`.
 */
export function commitUserValue<T>(control: AbstractControl<T> | null | undefined, value: T): void {
  if (!control) {
    return;
  }
  markUserEdit(control);
  control.patchValue(value);
}
